import { useState, useEffect } from "react";
import {
	useNavigate,
	useParams,
	useSearchParams,
	useLocation,
} from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
	Box,
	Typography,
	Button,
	TextField,
	Card,
	CardContent,
	Grid,
	Chip,
	IconButton,
	Paper,
	Divider,
	CircularProgress,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Select,
	MenuItem,
	FormControl,
	InputLabel,
	Tooltip,
	Checkbox,
	Menu,
	InputAdornment,
	FormControlLabel,
} from "@mui/material";
import {
	ArrowBack,
	Save,
	Delete,
	Add,
	DragIndicator,
	Edit,
	Storage,
	ExpandMore,
	ExpandLess,
	AutoFixHigh,
	ArrowUpward,
	ArrowDownward,
	ContentCopy,
	Visibility,
	VisibilityOff,
	MoreVert,
	FiberManualRecord,
	AdsClick,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import testService from "../services/testService";
import healingService from "../services/healingService";
import { fetchProjects } from "../store/slices/projectSlice";
import { fetchFolders } from "../store/slices/folderSlice";
import { fetchDataSets } from "../store/slices/dataSetSlice";
import ReplayRecordDialog from "../components/ReplayRecordDialog";
import CloneDialog from "../components/CloneDialog";
import OwnerBadge from "../components/OwnerBadge";
import CheckStepFields from "../components/CheckStepFields";
import { CHECK_STEP_LABELS, checkStepSummary } from "../components/StepCheckDetails";
import { isViewOnly, canModify } from "../utils/permissions";
import {
	formatDateTime,
	formatElapsed,
	toBackendDateTime,
} from "../utils/dateUtils";
import {
	applyPickedTarget,
	hasFallbackLocators,
	isElementStep,
	newStepId,
	toTestSteps,
	withoutRecordedTarget,
	withStepIds,
} from "../utils/stepUtils";

export default function TestEditor() {
	const { id } = useParams();
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const location = useLocation();
	const { enqueueSnackbar } = useSnackbar();
	const dispatch = useDispatch();
	const { projects } = useSelector((state) => state.projects);
	const { folders } = useSelector((state) => state.folders);
	const { dataSets } = useSelector((state) => state.datasets);
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [test, setTest] = useState({
		name: "",
		description: "",
		startUrl: "",
		steps: [],
		tags: [],
		folder: "",
		status: "DRAFT",
		projectId: "",
		folderId: "",
	});
	const [tagInput, setTagInput] = useState("");
	const [stepDialogOpen, setStepDialogOpen] = useState(false);

	const hasDataBinding = Boolean(test.dataBinding?.dataSetId);
	const boundVariables = hasDataBinding
		? Object.values(test.dataBinding?.columnMapping || {})
		: [];
	const variableHint =
		hasDataBinding && boundVariables.length > 0
			? `Use {{${boundVariables.join("}}, {{")}}}} for data-driven values`
			: hasDataBinding
				? "Use {{variableName}} for data-driven values"
				: "";
	const containsVariable = (step) =>
		/\{\{.+?\}\}/.test(
			`${step.value || ""}${step.url || ""}${step.selector || ""}${step.text || ""}`,
		);
	const [editingStepIndex, setEditingStepIndex] = useState(null);
	const [insertAfterIndex, setInsertAfterIndex] = useState(null);
	// Keyed by step id, so they follow steps that move
	const [expandedSteps, setExpandedSteps] = useState({});
	const [selectedStepIds, setSelectedStepIds] = useState([]);
	// Replay-then-record: { mode, anchorIndex, fromStepDialog }
	const [replay, setReplay] = useState(null);
	const [stepMenu, setStepMenu] = useState(null); // { anchorEl, index }
	// Steps added or re-targeted by a recording, until the next save/load
	const [changedSteps, setChangedSteps] = useState({});
	const [cloneOpen, setCloneOpen] = useState(false);
	// Another user's test (backend canModify=false): view, run and clone only.
	// The backend rejects changes anyway; this keeps the controls out of the way.
	const readOnly = isViewOnly(test);
	const [healingEvents, setHealingEvents] = useState([]);
	const [folderDialogOpen, setFolderDialogOpen] = useState(false);
	const [newFolderName, setNewFolderName] = useState("");
	const [creatingFolder, setCreatingFolder] = useState(false);
	const [currentStep, setCurrentStep] = useState({
		type: "click",
		selector: "",
		value: "",
		url: "",
		text: "",
		timestamp: Date.now(),
	});

	useEffect(() => {
		dispatch(fetchProjects());
		dispatch(fetchDataSets({ workspaceId: "default", projectId: "" }));
	}, [dispatch]);

	useEffect(() => {
		if (test.projectId) {
			dispatch(fetchFolders(test.projectId));
		}
	}, [test.projectId, dispatch]);

	useEffect(() => {
		if (id && id !== "new") {
			loadTest();
		} else {
			// Check if projectId is passed via URL
			const projectId = searchParams.get("project");
			if (projectId) {
				setTest((prev) => ({ ...prev, projectId }));
			}

			// Check for data in URL query parameters (from extension)
			const dataParam = searchParams.get("data");
			if (dataParam) {
				try {
					const decodedData = JSON.parse(
						decodeURIComponent(dataParam),
					);
					if (Array.isArray(decodedData) && decodedData.length > 0) {
						// Find the first navigation to set startUrl
						const firstNav = decodedData.find(
							(step) => step.type === "navigation",
						);
						setTest((prev) => ({
							...prev,
							startUrl: firstNav?.url || "",
							steps: withStepIds(decodedData),
							name: `Recorded Test ${new Date().toLocaleString()}`,
							projectId: projectId || prev.projectId,
						}));
						enqueueSnackbar("Test steps loaded from recording", {
							variant: "success",
						});
					}
				} catch (error) {
					console.error("Failed to parse data parameter:", error);
					enqueueSnackbar("Failed to load recorded steps", {
						variant: "error",
					});
				}
			}
		}
	}, [id, searchParams]);

	const loadTest = async () => {
		try {
			setLoading(true);
			const data = await testService.getTest(id);
			// API tests have their own editor; links everywhere point here
			if (data.testType === "API") {
				navigate(`/api-tests/${id}${window.location.search}`, { replace: true });
				return;
			}
			setTest({ ...data, steps: withStepIds(data.steps || []) });
			setSelectedStepIds([]);
			setChangedSteps({});
			// Load healing events for this test
			try {
				const events = await healingService.getHealingEvents({
					testId: id,
				});
				setHealingEvents(events || []);
			} catch {
				/* ignore if healing endpoint not available */
			}
		} catch (error) {
			enqueueSnackbar(error.response?.data?.message || "Failed to load test", { variant: "error" });
			navigate("/tests");
		} finally {
			setLoading(false);
		}
	};

	const toggleStepExpand = (stepId) => {
		setExpandedSteps((prev) => ({ ...prev, [stepId]: !prev[stepId] }));
	};

	const getLocatorScoreColor = (score) => {
		if (score >= 75) return "success";
		if (score >= 50) return "warning";
		return "error";
	};

	const handleApplyHealing = async (eventId) => {
		try {
			await healingService.applyHealing(eventId);
			enqueueSnackbar("Healing applied", { variant: "success" });
			loadTest();
		} catch {
			enqueueSnackbar("Failed to apply healing", { variant: "error" });
		}
	};

	const handleRejectHealing = async (eventId) => {
		try {
			await healingService.rejectHealing(eventId);
			enqueueSnackbar("Healing rejected", { variant: "info" });
			loadTest();
		} catch {
			enqueueSnackbar("Failed to reject healing", { variant: "error" });
		}
	};

	const handleSave = async () => {
		if (!test.name.trim()) {
			enqueueSnackbar("Please enter a test name", { variant: "warning" });
			return;
		}

		try {
			setSaving(true);
			const projectId = searchParams.get("project");
			const folderId = searchParams.get("folder");

			// Build navigation URL with filters
			const buildTestsUrl = () => {
				let url = "/tests";
				const params = new URLSearchParams();
				if (projectId) params.set("project", projectId);
				if (folderId) params.set("folder", folderId);
				if (params.toString()) url += `?${params.toString()}`;
				return url;
			};

			if (id && id !== "new") {
				await testService.updateTest(id, test);
				enqueueSnackbar("Test updated successfully", {
					variant: "success",
				});
				// Always navigate back to tests page with filters preserved
				navigate(buildTestsUrl());
			} else {
				await testService.createTest(test);
				enqueueSnackbar("Test created successfully", {
					variant: "success",
				});
				// Always navigate back to tests page with filters preserved
				navigate(buildTestsUrl());
			}
		} catch (error) {
			console.error("Save test error:", error);
			const errorMessage =
				error.response?.data?.message ||
				error.response?.data?.error ||
				error.message ||
				"Failed to save test";
			enqueueSnackbar(errorMessage, { variant: "error" });

			// Don't let the error propagate to avoid logout
			// The interceptor will handle 401 errors
		} finally {
			setSaving(false);
		}
	};

	const handleAddTag = () => {
		if (tagInput.trim() && !test.tags.includes(tagInput.trim())) {
			setTest({ ...test, tags: [...test.tags, tagInput.trim()] });
			setTagInput("");
		}
	};

	const handleCreateFolder = async () => {
		if (!newFolderName.trim()) {
			enqueueSnackbar("Please enter a folder name", {
				variant: "warning",
			});
			return;
		}
		if (!test.projectId) {
			enqueueSnackbar("Please select a project first", {
				variant: "warning",
			});
			return;
		}

		try {
			setCreatingFolder(true);
			const folderData = {
				name: newFolderName.trim(),
				projectId: test.projectId,
				parentFolderId: null,
			};

			const { createFolder } =
				await import("../store/slices/folderSlice");
			const result = await dispatch(createFolder(folderData)).unwrap();

			// Set the newly created folder as selected
			setTest({ ...test, folderId: result.id });
			enqueueSnackbar("Folder created successfully", {
				variant: "success",
			});
			setFolderDialogOpen(false);
			setNewFolderName("");
		} catch (error) {
			enqueueSnackbar(error.message || "Failed to create folder", {
				variant: "error",
			});
		} finally {
			setCreatingFolder(false);
		}
	};

	const handleRemoveTag = (tagToRemove) => {
		setTest({
			...test,
			tags: test.tags.filter((tag) => tag !== tagToRemove),
		});
	};

	const handleRemoveStep = (index) => {
		const removedId = test.steps[index].id;
		const newSteps = test.steps.filter((_, i) => i !== index);
		setTest({ ...test, steps: newSteps });
		setSelectedStepIds((prev) => prev.filter((i) => i !== removedId));
	};

	const handleAddStep = () => {
		setCurrentStep({
			type: "click",
			selector: "",
			value: "",
			url: "",
			text: "",
			timestamp: Date.now(),
		});
		setEditingStepIndex(null);
		setInsertAfterIndex(null);
		setStepDialogOpen(true);
	};

	const handleInsertStep = (afterIndex) => {
		setCurrentStep({
			type: "click",
			selector: "",
			value: "",
			url: "",
			text: "",
			timestamp: Date.now(),
		});
		setEditingStepIndex(null);
		setInsertAfterIndex(afterIndex);
		setStepDialogOpen(true);
	};

	const handleEditStep = (index) => {
		const step = { ...test.steps[index] };
		// Normalize assert steps: expand type:"assert" + assertType into
		// the compound type the dropdown expects (e.g. "assert-text")
		if (step.type === "assert") {
			const sub = step.assertType || "text"; // default legacy asserts to "text"
			step.type = `assert-${sub}`;
		}
		setCurrentStep(step);
		setEditingStepIndex(index);
		setStepDialogOpen(true);
	};

	const handleSaveStep = () => {
		if (!currentStep.type) {
			enqueueSnackbar("Please select a step type", {
				variant: "warning",
			});
			return;
		}

		// Validate based on step type
		const requiresSelector = [
			"click", "doubleClick", "rightClick",
			"type", "input", "clearInput", "select", "change",
			"hover", "scroll", "dragDrop",
			"assert-text", "assert-value", "assert-visible",
			"assert-not-visible", "assert-table", "assert-count", "assert-attribute",
		];
		if (requiresSelector.includes(currentStep.type) && !currentStep.selector) {
			enqueueSnackbar("Please enter a selector", { variant: "warning" });
			return;
		}

		if (currentStep.type === "navigation" && !currentStep.url && !currentStep.fromClipboard) {
			enqueueSnackbar("Please enter a URL", { variant: "warning" });
			return;
		}

		if (currentStep.type === "switchTab" && !(currentStep.tabIndex >= 0)) {
			enqueueSnackbar("Please enter the tab number", { variant: "warning" });
			return;
		}

		if (currentStep.type === "keyboard" && !currentStep.key && !currentStep.value) {
			enqueueSnackbar("Please enter a key or shortcut", { variant: "warning" });
			return;
		}

		if (["assert-url", "assert-title"].includes(currentStep.type) && !currentStep.value) {
			enqueueSnackbar("Please enter the expected value", { variant: "warning" });
			return;
		}

		const stepToSave = {
			...currentStep,
			id: currentStep.id || newStepId(),
			timestamp: Date.now(),
		};

		// For navigation steps, also set value field for backend compatibility
		if (stepToSave.type === "navigation" && stepToSave.url) {
			stepToSave.value = stepToSave.url;
		}

		// Normalize assert step types for executor compatibility
		if (stepToSave.type.startsWith("assert-")) {
			stepToSave.assertType = stepToSave.type.replace("assert-", "");
			stepToSave.type = "assert";
		}

		if (editingStepIndex !== null) {
			// Update existing step. A hand-changed selector makes the recorded
			// locators/metadata describe the wrong element — drop them.
			const original = test.steps[editingStepIndex];
			const retargeted =
				isElementStep(stepToSave) &&
				stepToSave.selector !== original.selector &&
				(hasFallbackLocators(original) || original.elementMeta) &&
				// not re-picked in the browser (that brings its own locators)
				stepToSave.locators === original.locators &&
				stepToSave.elementMeta === original.elementMeta;
			const newSteps = [...test.steps];
			newSteps[editingStepIndex] = retargeted
				? withoutRecordedTarget(stepToSave)
				: stepToSave;
			setTest({ ...test, steps: newSteps });
			enqueueSnackbar("Step updated", { variant: "success" });
		} else if (insertAfterIndex !== null) {
			// Insert step after a specific index
			const newSteps = [...test.steps];
			newSteps.splice(insertAfterIndex + 1, 0, stepToSave);
			setTest({ ...test, steps: newSteps });
			enqueueSnackbar("Step added", { variant: "success" });
		} else {
			// Append new step to end
			setTest({ ...test, steps: [...test.steps, stepToSave] });
			enqueueSnackbar("Step added", { variant: "success" });
		}

		setStepDialogOpen(false);
		setEditingStepIndex(null);
		setInsertAfterIndex(null);
	};

	const handleCancelStep = () => {
		setStepDialogOpen(false);
		setEditingStepIndex(null);
		setInsertAfterIndex(null);
	};

	const isStepSelected = (step) => selectedStepIds.includes(step.id);

	const toggleStepSelected = (stepId) => {
		setSelectedStepIds((prev) =>
			prev.includes(stepId)
				? prev.filter((i) => i !== stepId)
				: [...prev, stepId],
		);
	};

	const allStepsSelected =
		test.steps.length > 0 && selectedStepIds.length === test.steps.length;

	const toggleSelectAllSteps = () => {
		setSelectedStepIds(allStepsSelected ? [] : test.steps.map((s) => s.id));
	};

	const handleToggleStepDisabled = (index) => {
		const newSteps = [...test.steps];
		newSteps[index] = { ...newSteps[index], disabled: !newSteps[index].disabled };
		setTest({ ...test, steps: newSteps });
	};

	const handleDeleteSelected = () => {
		setTest({ ...test, steps: test.steps.filter((s) => !isStepSelected(s)) });
		enqueueSnackbar(`${selectedStepIds.length} step(s) deleted`, {
			variant: "success",
		});
		setSelectedStepIds([]);
	};

	// Copies go right after the last selected step, in their original order,
	// and become the new selection.
	const handleDuplicateSelected = () => {
		const copies = test.steps
			.filter(isStepSelected)
			.map((s) => ({ ...s, id: newStepId() }));
		const newSteps = [...test.steps];
		newSteps.splice(test.steps.findLastIndex(isStepSelected) + 1, 0, ...copies);
		setTest({ ...test, steps: newSteps });
		setSelectedStepIds(copies.map((c) => c.id));
		enqueueSnackbar(`${copies.length} step(s) duplicated`, {
			variant: "success",
		});
	};

	const handleSetSelectedDisabled = (disabled) => {
		setTest({
			...test,
			steps: test.steps.map((s) =>
				isStepSelected(s) ? { ...s, disabled } : s,
			),
		});
	};

	// Moves every selected step one place, keeping their relative order; a
	// selected step already at the edge stays put.
	const handleMoveSelected = (direction) => {
		const newSteps = [...test.steps];
		if (direction < 0) {
			for (let i = 1; i < newSteps.length; i++) {
				if (isStepSelected(newSteps[i]) && !isStepSelected(newSteps[i - 1])) {
					[newSteps[i - 1], newSteps[i]] = [newSteps[i], newSteps[i - 1]];
				}
			}
		} else {
			for (let i = newSteps.length - 2; i >= 0; i--) {
				if (isStepSelected(newSteps[i]) && !isStepSelected(newSteps[i + 1])) {
					[newSteps[i], newSteps[i + 1]] = [newSteps[i + 1], newSteps[i]];
				}
			}
		}
		setTest({ ...test, steps: newSteps });
	};

	const openReplay = (mode, anchorIndex, fromStepDialog = false) => {
		setStepMenu(null);
		setReplay({ mode, anchorIndex, fromStepDialog });
	};

	// The step dialog names assert types "assert-<sub>"
	const isElementDialogStep = (step) =>
		["visualCheck", "accessibilityCheck"].includes(step.type) ||
		isElementStep(
			step.type?.startsWith("assert-")
				? { type: "assert", assertType: step.type.slice(7) }
				: step,
		);

	const markChanged = (ids, label) => {
		setChangedSteps((prev) => ({
			...prev,
			...Object.fromEntries(ids.map((i) => [i, label])),
		}));
	};

	// Merges what ReplayRecordDialog produced into the (unsaved) step list.
	const handleReplayComplete = ({ recorded, fromIndex, picked, recordingStartedAt }) => {
		const { mode, anchorIndex, fromStepDialog } = replay;
		setReplay(null);

		if (picked) {
			if (fromStepDialog) {
				setCurrentStep((prev) => applyPickedTarget(prev, picked));
				enqueueSnackbar("Element picked — click Update to keep it", {
					variant: "info",
				});
				return;
			}
			const stepId = test.steps[anchorIndex].id;
			setTest((prev) => {
				const steps = [...prev.steps];
				steps[anchorIndex] = applyPickedTarget(steps[anchorIndex], picked);
				return { ...prev, steps };
			});
			markChanged([stepId], "Changed");
			enqueueSnackbar(
				`Element picked for step ${anchorIndex + 1} — review and Save`,
				{ variant: "success" },
			);
			return;
		}

		const newSteps = toTestSteps(recorded);
		if (newSteps.length === 0) {
			enqueueSnackbar("Nothing was recorded — the test is unchanged", {
				variant: "info",
			});
			return;
		}
		// Recording starts after the replayed steps, or at the step where the
		// user chose "Start recording from here" (replacing it and the rest of
		// the replayed range).
		const from = mode === "single" ? anchorIndex : (fromIndex ?? anchorIndex + 1);
		// This session's recording time adds to the test's total; the replay
		// before it isn't recording time
		const lastStepAt = Math.max(...newSteps.map((s) => s.timestamp));
		const took = recordingStartedAt ? Math.max(0, lastStepAt - recordingStartedAt) : 0;
		setTest((prev) => ({
			...prev,
			recordedAt:
				prev.recordedAt ||
				(recordingStartedAt ? toBackendDateTime(recordingStartedAt) : prev.recordedAt),
			recordingDuration: (prev.recordingDuration || 0) + took,
			steps: [
				...prev.steps.slice(0, from),
				...newSteps,
				...(mode === "replace" ? [] : prev.steps.slice(anchorIndex + 1)),
			],
		}));
		setSelectedStepIds([]);
		markChanged(
			newSteps.map((s) => s.id),
			"New",
		);
		enqueueSnackbar(`${newSteps.length} step(s) recorded — review and Save`, {
			variant: "success",
		});
	};

	const [draggedStepIndex, setDraggedStepIndex] = useState(null);

	const handleDragStart = (e, index) => {
		setDraggedStepIndex(index);
		e.dataTransfer.effectAllowed = "move";
	};

	const handleDragOver = (e) => {
		e.preventDefault();
		e.dataTransfer.dropEffect = "move";
	};

	const handleDrop = (e, targetIndex) => {
		e.preventDefault();
		if (draggedStepIndex === null || draggedStepIndex === targetIndex) {
			setDraggedStepIndex(null);
			return;
		}

		const draggedStep = test.steps[draggedStepIndex];
		if (isStepSelected(draggedStep) && selectedStepIds.length > 1) {
			const target = test.steps[targetIndex];
			if (isStepSelected(target)) {
				setDraggedStepIndex(null);
				return;
			}
			// Like a single-step drag: dropped lower lands after the target,
			// dropped higher lands before it.
			const moving = test.steps.filter(isStepSelected);
			const rest = test.steps.filter((s) => !isStepSelected(s));
			const at =
				rest.indexOf(target) + (draggedStepIndex < targetIndex ? 1 : 0);
			rest.splice(at, 0, ...moving);
			setTest({ ...test, steps: rest });
			enqueueSnackbar(`${moving.length} steps moved`, { variant: "success" });
			setDraggedStepIndex(null);
			return;
		}

		const items = Array.from(test.steps);
		const draggedItem = items[draggedStepIndex];
		items.splice(draggedStepIndex, 1);
		items.splice(targetIndex, 0, draggedItem);

		setTest({ ...test, steps: items });
		enqueueSnackbar("Step reordered", { variant: "success" });
		setDraggedStepIndex(null);
	};

	const handleDragEnd = (e) => {
		setDraggedStepIndex(null);
	};

	if (loading) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
				<CircularProgress />
			</Box>
		);
	}

	return (
		<Box>
			{/* Header */}
			<Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
				<IconButton
					onClick={() => {
						const projectId = searchParams.get("project");
						const folderId = searchParams.get("folder");
						if (projectId && folderId) {
							navigate(
								`/tests?project=${projectId}&folder=${folderId}`,
							);
						} else if (projectId) {
							navigate(`/tests?project=${projectId}`);
						} else {
							navigate("/tests");
						}
					}}
				>
					<ArrowBack />
				</IconButton>
				<Box sx={{ flexGrow: 1 }}>
					<Typography variant="h4" fontWeight={700}>
						{id === "new" ||
						!id ||
						location.pathname === "/tests/new"
							? "Create Test"
							: readOnly
								? "View Test"
								: "Edit Test"}
					</Typography>
					<Typography variant="body2" color="text.secondary">
						{id === "new" ||
						!id ||
						location.pathname === "/tests/new"
							? "Create a new test case"
							: readOnly
								? "Someone else's test: you can view and run it, or clone it to make your own copy to edit"
								: "Edit your test configuration"}
					</Typography>
					<OwnerBadge item={test} sx={{ mt: 1 }} />
				</Box>
				{test.id && (
					<Button
						variant={readOnly ? "contained" : "outlined"}
						startIcon={<ContentCopy />}
						onClick={() => setCloneOpen(true)}
						sx={{ mr: 1 }}
					>
						Clone
					</Button>
				)}
				{!readOnly && (
					<Button
						variant="contained"
						startIcon={<Save />}
						onClick={handleSave}
						disabled={saving}
					>
						{saving ? "Saving..." : "Save"}
					</Button>
				)}
			</Box>

			<Grid container spacing={3}>
				{/* Basic Information */}
				<Grid item xs={12} md={8}>
					<Card>
						<CardContent>
							<Typography
								variant="h6"
								fontWeight={600}
								gutterBottom
							>
								Basic Information
							</Typography>
							<Box
								sx={{
									display: "flex",
									flexDirection: "column",
									gap: 2,
									mt: 2,
								}}
							>
								<TextField disabled={readOnly}
									label="Test Name"
									fullWidth
									required
									value={test.name}
									onChange={(e) =>
										setTest({
											...test,
											name: e.target.value,
										})
									}
									placeholder="e.g., Login Flow Test"
								/>
								<TextField disabled={readOnly}
									label="Description"
									fullWidth
									multiline
									rows={3}
									value={test.description}
									onChange={(e) =>
										setTest({
											...test,
											description: e.target.value,
										})
									}
									placeholder="Describe what this test does..."
								/>
								<TextField disabled={readOnly}
									label="Start URL"
									fullWidth
									value={test.startUrl}
									onChange={(e) =>
										setTest({
											...test,
											startUrl: e.target.value,
										})
									}
									placeholder="https://example.com"
								/>

								<Grid container spacing={2}>
									<Grid item xs={12} md={6}>
										<FormControl fullWidth>
											<InputLabel>Project</InputLabel>
											<Select disabled={readOnly}
												value={test.projectId || ""}
												onChange={(e) =>
													setTest({
														...test,
														projectId:
															e.target.value,
														folderId: "", // Reset folder when project changes
													})
												}
												label="Project"
											>
												<MenuItem value="">
													<em>None</em>
												</MenuItem>
												{projects
													.filter((p) => canModify(p) || p.id === test.projectId)
													.map((project) => (
													<MenuItem
														key={project.id}
														value={project.id}
													>
														<Box
															sx={{
																display: "flex",
																alignItems:
																	"center",
																gap: 1,
															}}
														>
															<Box
																sx={{
																	width: 8,
																	height: 8,
																	borderRadius:
																		"50%",
																	bgcolor:
																		project.color ||
																		"#3b82f6",
																}}
															/>
															{project.name}
														</Box>
													</MenuItem>
												))}
											</Select>
										</FormControl>
									</Grid>
									<Grid item xs={12} md={6}>
										<Box sx={{ display: "flex", gap: 1 }}>
											<FormControl
												fullWidth
												disabled={!test.projectId}
											>
												<InputLabel>Folder</InputLabel>
												<Select disabled={readOnly}
													value={test.folderId || ""}
													onChange={(e) =>
														setTest({
															...test,
															folderId:
																e.target.value,
														})
													}
													label="Folder"
												>
													<MenuItem value="">
														<em>None</em>
													</MenuItem>
													{folders
														.filter(
															(f) =>
																f.projectId ===
																test.projectId && f.kind !== "API",
														)
														.map((folder) => (
															<MenuItem
																key={folder.id}
																value={
																	folder.id
																}
															>
																{folder.path ||
																	folder.name}
															</MenuItem>
														))}
												</Select>
											</FormControl>
											<Button
												variant="outlined"
												disabled={readOnly || (!test.projectId)}
												onClick={() =>
													setFolderDialogOpen(true)
												}
												sx={{ minWidth: "auto", px: 2 }}
											>
												<Add />
											</Button>
										</Box>
									</Grid>
								</Grid>
							</Box>
						</CardContent>
					</Card>

					{/* Test Steps */}
					<Card sx={{ mt: 3 }}>
						<CardContent>
							<Box
								sx={{
									display: "flex",
									justifyContent: "space-between",
									alignItems: "center",
									mb: 2,
								}}
							>
								<Box sx={{ display: "flex", alignItems: "center" }}>
									{test.steps.length > 0 && !readOnly && (
										<Checkbox
											size="small"
											checked={allStepsSelected}
											indeterminate={
												selectedStepIds.length > 0 &&
												!allStepsSelected
											}
											onChange={toggleSelectAllSteps}
											inputProps={{ "aria-label": "Select all steps" }}
											sx={{ ml: -1 }}
										/>
									)}
									<Typography variant="h6" fontWeight={600}>
										Test Steps
										<Typography
											component="span"
											variant="body2"
											color="text.secondary"
											sx={{ ml: 1 }}
										>
											({test.steps.length})
										</Typography>
									</Typography>
								</Box>
								{!readOnly && <Box sx={{ display: "flex", gap: 1 }}>
									<Tooltip describeChild title="Replay the whole test in a browser window, then record more steps at the end">
										<span>
											<Button
												variant="outlined"
												color="error"
												startIcon={<FiberManualRecord />}
												onClick={() =>
													openReplay("append", test.steps.length - 1)
												}
												disabled={!test.steps.length && !test.startUrl}
												size="small"
											>
												Record more
											</Button>
										</span>
									</Tooltip>
									<Button
										variant="outlined"
										startIcon={<Add />}
										onClick={handleAddStep}
										size="small"
									>
										Add Step
									</Button>
								</Box>}
							</Box>

							{test.recordedAt && (
								<Tooltip
									describeChild
									placement="bottom-start"
									title="When the test was first recorded, and the time spent recording it (recordings added in the editor count too)"
								>
									<Typography
										variant="body2"
										color="text.secondary"
										data-testid="recorded-time"
										sx={{ mt: -1, mb: 2, width: "fit-content" }}
									>
										Recorded {formatDateTime(test.recordedAt)}
										{test.recordingDuration != null &&
											` · ${formatElapsed(test.recordingDuration)} of recording`}
									</Typography>
								</Tooltip>
							)}

							{selectedStepIds.length > 0 && (
								<Paper
									variant="outlined"
									data-testid="step-selection-toolbar"
									sx={{
										display: "flex",
										alignItems: "center",
										flexWrap: "wrap",
										gap: 1,
										px: 2,
										py: 1,
										mb: 1,
									}}
								>
									<Typography variant="body2" fontWeight={600} sx={{ mr: 1 }}>
										{selectedStepIds.length} selected
									</Typography>
									<Tooltip title="Move up">
										<IconButton size="small" onClick={() => handleMoveSelected(-1)}>
											<ArrowUpward fontSize="small" />
										</IconButton>
									</Tooltip>
									<Tooltip title="Move down">
										<IconButton size="small" onClick={() => handleMoveSelected(1)}>
											<ArrowDownward fontSize="small" />
										</IconButton>
									</Tooltip>
									<Button size="small" startIcon={<ContentCopy />} onClick={handleDuplicateSelected}>
										Duplicate
									</Button>
									<Button size="small" startIcon={<Visibility />} onClick={() => handleSetSelectedDisabled(false)}>
										Enable
									</Button>
									<Button size="small" startIcon={<VisibilityOff />} onClick={() => handleSetSelectedDisabled(true)}>
										Disable
									</Button>
									<Button size="small" color="error" startIcon={<Delete />} onClick={handleDeleteSelected}>
										Delete
									</Button>
									<Box sx={{ flex: 1 }} />
									<Button size="small" onClick={() => setSelectedStepIds([])}>
										Clear
									</Button>
								</Paper>
							)}

							{test.steps.length === 0 ? (
								<Paper
									sx={{
										p: 4,
										textAlign: "center",
										bgcolor: "action.hover",
										mt: 2,
									}}
								>
									<Typography
										variant="body2"
										color="text.secondary"
									>
										No steps recorded yet. Use the browser
										extension to record actions.
									</Typography>
								</Paper>
							) : (
								<Box sx={{ mt: 2, width: "100%" }}>
									{test.steps.map((step, index) => {
										const selectors =
											step.options?.selectors ||
											step.locators ||
											[];
										const hasLocators =
											selectors.length > 0;
										const topScore = hasLocators
											? Math.max(
													...selectors.map(
														(s) => s.score || 0,
													),
												)
											: null;
										const pendingHealing =
											healingEvents.filter(
												(e) =>
													(e.stepUid
														? e.stepUid === step.id
														: e.stepId ===
															String(index)) &&
													e.status ===
														"PENDING_REVIEW",
											);
										const isExpandable =
											hasLocators ||
											pendingHealing.length > 0;

										return (
											<Box key={step.id || index} sx={{ mb: 0.5 }}>
												<Box
													draggable={!readOnly}
													onDragStart={(e) =>
														handleDragStart(
															e,
															index,
														)
													}
													onDragOver={handleDragOver}
													onDrop={(e) =>
														handleDrop(e, index)
													}
													onDragEnd={handleDragEnd}
													sx={{
														bgcolor:
															draggedStepIndex ===
															index
																? "primary.light"
																: isStepSelected(step)
																	? "action.selected"
																	: "action.hover",
														borderLeft: changedSteps[step.id]
															? "4px solid"
															: undefined,
														borderLeftColor: "secondary.main",
														borderRadius:
															expandedSteps[step.id]
																? "4px 4px 0 0"
																: 1,
														display: "flex",
														alignItems: "center",
														gap: 1,
														padding: 2,
														width: "100%",
														boxSizing: "border-box",
														cursor: readOnly
															? "default"
															: draggedStepIndex ===
																  index
																? "grabbing"
																: "grab",
														opacity:
															draggedStepIndex ===
															index
																? 0.5
																: step.disabled
																	? 0.55
																	: 1,
														transition:
															"all 0.2s ease",
													}}
												>
													{!readOnly && (
														<>
															<Checkbox
																size="small"
																checked={isStepSelected(step)}
																onChange={() =>
																	toggleStepSelected(step.id)
																}
																inputProps={{
																	"aria-label": `Select step ${index + 1}`,
																}}
																sx={{ ml: -1.5, p: 0.5 }}
															/>
															<DragIndicator
																sx={{
																	color: "text.secondary",
																	ml: -1,
																	mr: 1,
																}}
															/>
														</>
													)}
													<Box
														sx={{
															display: "flex",
															alignItems:
																"center",
															gap: 1,
															flex: 1,
															minWidth: 0,
														}}
													>
														<Chip
															label={
																step.type ===
																	"assert" &&
																step.assertType
																	? `assert ${step.assertType}`
																	: CHECK_STEP_LABELS[step.type] ||
																		step.type
															}
															size="small"
															color={
																step.type ===
																"assert"
																	? "success"
																	: CHECK_STEP_LABELS[step.type]
																		? "info"
																		: "primary"
															}
															sx={{
																fontWeight: 600,
															}}
														/>
														{containsVariable(
															step,
														) && (
															<Chip
																label="{{x}}"
																size="small"
																color="warning"
																variant="outlined"
																sx={{
																	fontFamily:
																		"monospace",
																	fontWeight: 600,
																}}
															/>
														)}
														{topScore !== null && (
															<Chip
																label={topScore}
																size="small"
																color={getLocatorScoreColor(
																	topScore,
																)}
																variant="outlined"
																sx={{
																	minWidth: 36,
																	fontWeight: 600,
																}}
																title="Locator stability score"
															/>
														)}
														{pendingHealing.length >
															0 && (
															<Chip
																icon={
																	<AutoFixHigh />
																}
																label="Heal"
																size="small"
																color="info"
																variant="outlined"
																sx={{
																	fontWeight: 600,
																}}
															/>
														)}
														{changedSteps[step.id] && (
															<Chip
																label={changedSteps[step.id]}
																size="small"
																color="secondary"
															/>
														)}
														{step.disabled && (
															<Chip
																label="Disabled"
																size="small"
																variant="outlined"
															/>
														)}
														{isElementStep(step) &&
															!hasFallbackLocators(
																step,
															) && (
																<Tooltip describeChild title="Only the selector identifies this element — no recorded locators for self-healing to fall back on. Click to pick the element in the browser and regenerate them.">
																	<Chip
																		label="No fallback locators"
																		size="small"
																		color="warning"
																		variant="outlined"
																		icon={<AdsClick />}
																		onClick={
																			readOnly
																				? undefined
																				: () => openReplay("pick", index)
																		}
																	/>
																</Tooltip>
															)}
														<Box
															sx={{
																flex: 1,
																minWidth: 0,
																overflow:
																	"hidden",
															}}
														>
															<Typography
																variant="body2"
																noWrap
																title={
																	step.selector ||
																	step.url ||
																	step.value ||
																	""
																}
															>
																{step.type === "newTab"
																	? "Open a new tab"
																	: step.type === "switchTab"
																	? `Switch to tab ${step.tabIndex + 1}`
																	: step.type === "navigation" && step.fromClipboard
																	? "Open the copied link"
																	: step.type ===
																"navigation"
																	? step.url ||
																		step.value ||
																		"No URL"
																	: step.type === "keyboard"
																		? step.key ||
																			step.value ||
																			"No key"
																		: step.type === "scroll"
																			? step.selector
																				? `${step.selector} → (${step.x || 0}, ${step.y || 0})`
																				: `window → (${step.x || 0}, ${step.y || 0})`
																			: step.type ===
																						"assert" &&
																				  step.assertType ===
																						"table"
																				? step.selector +
																					" (table data)"
																				: step.type === "assert" &&
																				  ["url", "title"].includes(step.assertType)
																					? step.value || "No value"
																					: CHECK_STEP_LABELS[step.type]
																						? checkStepSummary(step)
																						: step.selector ||
																							step.value ||
																							"No details"}
															</Typography>
															{step.type ===
																"assert" &&
															step.assertType ===
																"table" ? (
																<Typography
																	variant="caption"
																	color="text.secondary"
																>
																	{(() => {
																		try {
																			const d =
																				JSON.parse(
																					step.value,
																				);
																			return `${d.headers?.length || 0} columns, ${d.rows?.length || 0} rows`;
																		} catch {
																			return "Table assertion";
																		}
																	})()}
																</Typography>
															) : (
																(step.value ||
																	step.url) && (
																	<Typography
																		variant="caption"
																		color="text.secondary"
																	>
																		{step.type ===
																		"navigation"
																			? step.fromClipboard
																				? "When recorded: "
																				: "URL: "
																			: step.type ===
																				  "assert"
																				? "Expected: "
																				: "Value: "}
																		{(
																			step.url ||
																			step.value
																		)?.substring(
																			0,
																			50,
																		)}
																	</Typography>
																)
															)}
														</Box>
													</Box>
													<Box
														sx={{
															display: "flex",
															gap: 0.5,
															flexShrink: 0,
														}}
													>
														{isExpandable && (
															<IconButton
																size="small"
																onClick={() =>
																	toggleStepExpand(
																		step.id,
																	)
																}
															>
																{expandedSteps[
																	step.id
																] ? (
																	<ExpandLess />
																) : (
																	<ExpandMore />
																)}
															</IconButton>
														)}
														{!readOnly && <>
														<Tooltip title="Record from here">
															<IconButton
																size="small"
																aria-label={`Recording options for step ${index + 1}`}
																onClick={(e) =>
																	setStepMenu({
																		anchorEl: e.currentTarget,
																		index,
																	})
																}
															>
																<MoreVert />
															</IconButton>
														</Tooltip>
														<Tooltip
															title={
																step.disabled
																	? "Enable step"
																	: "Disable step (skipped when the test runs)"
															}
														>
															<IconButton
																size="small"
																onClick={() =>
																	handleToggleStepDisabled(
																		index,
																	)
																}
															>
																{step.disabled ? (
																	<VisibilityOff />
																) : (
																	<Visibility />
																)}
															</IconButton>
														</Tooltip>
														<IconButton
															size="small"
															onClick={() =>
																handleEditStep(
																	index,
																)
															}
														>
															<Edit />
														</IconButton>
														<IconButton
															size="small"
															onClick={() =>
																handleRemoveStep(
																	index,
																)
															}
														>
															<Delete />
														</IconButton>
														</>}
													</Box>
												</Box>
												{/* Expanded Locator Panel */}
												{expandedSteps[step.id] &&
													isExpandable && (
														<Box
															sx={{
																bgcolor:
																	"background.paper",
																border: "1px solid",
																borderColor:
																	"divider",
																borderTop:
																	"none",
																borderRadius:
																	"0 0 4px 4px",
																px: 2,
																py: 1.5,
															}}
														>
															<Typography
																variant="caption"
																fontWeight={600}
																sx={{
																	mb: 1,
																	display:
																		"block",
																}}
															>
																Locator
																Candidates (
																{
																	selectors.length
																}
																)
															</Typography>
															{[...selectors]
																.sort(
																	(a, b) =>
																		(b.score ||
																			0) -
																		(a.score ||
																			0),
																)
																.map(
																	(
																		loc,
																		li,
																	) => (
																		<Box
																			key={
																				li
																			}
																			sx={{
																				display:
																					"flex",
																				alignItems:
																					"center",
																				gap: 1,
																				py: 0.5,
																				borderBottom:
																					li <
																					selectors.length -
																						1
																						? "1px solid"
																						: "none",
																				borderColor:
																					"divider",
																			}}
																		>
																			<Chip
																				label={
																					loc.type
																				}
																				size="small"
																				variant="outlined"
																				sx={{
																					minWidth: 70,
																					fontFamily:
																						"monospace",
																					fontSize: 11,
																				}}
																			/>
																			<Chip
																				label={
																					loc.score ??
																					"—"
																				}
																				size="small"
																				color={getLocatorScoreColor(
																					loc.score ||
																						0,
																				)}
																				sx={{
																					minWidth: 36,
																					fontWeight: 600,
																				}}
																			/>
																			<Typography
																				variant="caption"
																				fontFamily="monospace"
																				sx={{
																					flex: 1,
																					overflow:
																						"hidden",
																					textOverflow:
																						"ellipsis",
																					whiteSpace:
																						"nowrap",
																				}}
																				title={
																					loc.value
																				}
																			>
																				{
																					loc.value
																				}
																			</Typography>
																		</Box>
																	),
																)}
															{pendingHealing.length >
																0 && (
																<Box
																	sx={{
																		mt: 1,
																		p: 1.5,
																		bgcolor:
																			"info.light",
																		borderRadius: 1,
																	}}
																>
																	<Typography
																		variant="caption"
																		fontWeight={
																			600
																		}
																		color="info.contrastText"
																		sx={{
																			display:
																				"block",
																			mb: 1,
																		}}
																	>
																		Healing
																		suggestion
																		available
																	</Typography>
																	{pendingHealing.map(
																		(
																			evt,
																		) => (
																			<Box
																				key={
																					evt.id
																				}
																				sx={{
																					mb: 1,
																					p: 1,
																					bgcolor:
																						"background.paper",
																					borderRadius: 1,
																				}}
																			>
																				<Box
																					sx={{
																						display:
																							"flex",
																						alignItems:
																							"center",
																						gap: 1,
																						mb: 0.5,
																					}}
																				>
																					<Typography
																						variant="caption"
																						color="text.secondary"
																					>
																						Original:
																					</Typography>
																					<Typography
																						variant="caption"
																						fontFamily="monospace"
																						sx={{
																							textDecoration:
																								"line-through",
																							color: "error.main",
																						}}
																					>
																						{
																							evt.originalLocatorType
																						}

																						=
																						{evt.originalLocatorValue?.substring(
																							0,
																							50,
																						)}
																					</Typography>
																				</Box>
																				<Box
																					sx={{
																						display:
																							"flex",
																						alignItems:
																							"center",
																						gap: 1,
																						mb: 0.5,
																					}}
																				>
																					<Typography
																						variant="caption"
																						color="text.secondary"
																					>
																						Healed:
																					</Typography>
																					<Typography
																						variant="caption"
																						fontFamily="monospace"
																						sx={{
																							color: "success.main",
																							fontWeight: 600,
																						}}
																					>
																						{
																							evt.usedLocatorType
																						}

																						=
																						{evt.usedLocatorValue?.substring(
																							0,
																							50,
																						)}
																					</Typography>
																					{evt.similarityScore && (
																						<Chip
																							label={`${evt.similarityScore}%`}
																							size="small"
																							color="info"
																							sx={{
																								height: 18,
																								fontSize: 10,
																							}}
																						/>
																					)}
																				</Box>
																				{!readOnly && <Box
																					sx={{
																						display:
																							"flex",
																						gap: 1,
																						mt: 1,
																					}}
																				>
																					<Button
																						size="small"
																						variant="contained"
																						color="success"
																						sx={{
																							minWidth: 0,
																							px: 1.5,
																							py: 0.25,
																							fontSize: 11,
																						}}
																						onClick={() =>
																							handleApplyHealing(
																								evt.id,
																							)
																						}
																					>
																						Apply
																					</Button>
																					<Button
																						size="small"
																						variant="outlined"
																						color="error"
																						sx={{
																							minWidth: 0,
																							px: 1.5,
																							py: 0.25,
																							fontSize: 11,
																						}}
																						onClick={() =>
																							handleRejectHealing(
																								evt.id,
																							)
																						}
																					>
																						Reject
																					</Button>
																				</Box>}
																			</Box>
																		),
																	)}
																</Box>
															)}
														</Box>
													)}
											{!readOnly && index < test.steps.length - 1 && (
												<Box
													sx={{
														display: "flex",
														alignItems: "center",
														opacity: 0,
														"&:hover": { opacity: 1 },
														transition: "opacity 0.2s",
														my: 0.25,
														cursor: "pointer",
													}}
													onClick={() => handleInsertStep(index)}
												>
													<Divider sx={{ flex: 1 }} />
													<Tooltip title="Insert step here">
														<Chip
															icon={<Add sx={{ fontSize: "14px !important" }} />}
															label="insert"
															size="small"
															variant="outlined"
															color="primary"
															sx={{ mx: 1, height: 22, fontSize: 11, cursor: "pointer" }}
															onClick={(e) => { e.stopPropagation(); handleInsertStep(index); }}
														/>
													</Tooltip>
													<Divider sx={{ flex: 1 }} />
												</Box>
											)}
										</Box>
									);
								})}
								</Box>
							)}
						</CardContent>
					</Card>
				</Grid>

				{/* Metadata */}
				<Grid item xs={12} md={4}>
					<Card>
						<CardContent>
							<Typography
								variant="h6"
								fontWeight={600}
								gutterBottom
							>
								Tags
							</Typography>
							<Box sx={{ display: "flex", gap: 1, mt: 2 }}>
								<TextField disabled={readOnly}
									size="small"
									fullWidth
									placeholder="Add tag"
									value={tagInput}
									onChange={(e) =>
										setTagInput(e.target.value)
									}
									onKeyPress={(e) =>
										e.key === "Enter" && handleAddTag()
									}
								/>
								<IconButton disabled={readOnly} size="small" onClick={handleAddTag}>
									<Add />
								</IconButton>
							</Box>
							<Box
								sx={{
									display: "flex",
									gap: 1,
									flexWrap: "wrap",
									mt: 2,
								}}
							>
								{test.tags.map((tag) => (
									<Chip
										key={tag}
										label={tag}
										onDelete={readOnly ? undefined : () => handleRemoveTag(tag)}
										size="small"
									/>
								))}
							</Box>
						</CardContent>
					</Card>

					{test.metadata && (
						<Card sx={{ mt: 3 }}>
							<CardContent>
								<Typography
									variant="h6"
									fontWeight={600}
									gutterBottom
								>
									Statistics
								</Typography>
								<Box
									sx={{
										display: "flex",
										flexDirection: "column",
										gap: 2,
										mt: 2,
									}}
								>
									<Box>
										<Typography
											variant="caption"
											color="text.secondary"
										>
											Total Executions
										</Typography>
										<Typography
											variant="h6"
											fontWeight={600}
										>
											{test.metadata.executionCount || 0}
										</Typography>
									</Box>
									<Box>
										<Typography
											variant="caption"
											color="text.secondary"
										>
											Success Rate
										</Typography>
										<Typography
											variant="h6"
											fontWeight={600}
										>
											{test.metadata.executionCount > 0
												? Math.round(
														(test.metadata
															.successCount /
															test.metadata
																.executionCount) *
															100,
													)
												: 0}
											%
										</Typography>
									</Box>
									<Box>
										<Typography
											variant="caption"
											color="text.secondary"
										>
											Avg. Duration
										</Typography>
										<Typography
											variant="h6"
											fontWeight={600}
										>
											{test.metadata.averageDuration
												? `${Math.round(test.metadata.averageDuration / 1000)}s`
												: "N/A"}
										</Typography>
									</Box>
								</Box>
							</CardContent>
						</Card>
					)}

					{/* Data-Driven Testing */}
					<Card sx={{ mt: 3 }}>
						<CardContent>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 1,
									mb: 2,
								}}
							>
								<Storage color="primary" />
								<Typography variant="h6" fontWeight={600}>
									Data-Driven Testing
								</Typography>
							</Box>

							<FormControl fullWidth sx={{ mb: 2 }}>
								<InputLabel>Dataset</InputLabel>
								<Select disabled={readOnly}
									value={test.dataBinding?.dataSetId || ""}
									label="Dataset"
									onChange={(e) => {
										const dsId = e.target.value;
										if (!dsId) {
											setTest({
												...test,
												dataBinding: null,
											});
											return;
										}
										const ds = dataSets.find(
											(d) => d.id === dsId,
										);
										const identityMapping = {};
										(ds?.columns || []).forEach((col) => {
											identityMapping[col] = col;
										});
										setTest({
											...test,
											dataBinding: {
												...(test.dataBinding || {}),
												dataSetId: dsId,
												dataSetName: ds?.name || "",
												columnMapping: identityMapping,
												mode:
													test.dataBinding?.mode ||
													"ITERATE_ALL",
												selectionType: "ALL",
											},
										});
									}}
								>
									<MenuItem value="">
										<em>None (no data binding)</em>
									</MenuItem>
									{dataSets
										.filter((ds) => canModify(ds) || ds.id === test.dataBinding?.dataSetId)
										.map((ds) => (
										<MenuItem key={ds.id} value={ds.id}>
											{ds.name} ({ds.rows?.length || 0}{" "}
											rows)
										</MenuItem>
									))}
								</Select>
							</FormControl>

							{test.dataBinding?.dataSetId && (
								<>
									<FormControl fullWidth sx={{ mb: 2 }}>
										<InputLabel>Execution Mode</InputLabel>
										<Select disabled={readOnly}
											value={
												test.dataBinding?.mode ||
												"ITERATE_ALL"
											}
											label="Execution Mode"
											onChange={(e) =>
												setTest({
													...test,
													dataBinding: {
														...test.dataBinding,
														mode: e.target.value,
														specificRowIndex:
															e.target.value ===
															"SINGLE_ROW"
																? test
																		.dataBinding
																		?.specificRowIndex ||
																	0
																: null,
													},
												})
											}
										>
											<MenuItem value="ITERATE_ALL">
												Iterate All Rows
											</MenuItem>
											<MenuItem value="SINGLE_ROW">
												Single Row
											</MenuItem>
											<MenuItem value="RANDOM_ROW">
												Random Row
											</MenuItem>
										</Select>
									</FormControl>

									{test.dataBinding?.mode ===
										"SINGLE_ROW" && (
										<TextField disabled={readOnly}
											label="Row Number (1-based)"
											type="number"
											fullWidth
											value={
												test.dataBinding
													?.specificRowIndex ?? 0
											}
											onChange={(e) =>
												setTest({
													...test,
													dataBinding: {
														...test.dataBinding,
														specificRowIndex:
															parseInt(
																e.target.value,
																10,
															) || 0,
													},
												})
											}
											sx={{ mb: 2 }}
											inputProps={{ min: 0 }}
										/>
									)}

									<Typography
										variant="subtitle2"
										sx={{ mb: 1 }}
									>
										Column Mapping
									</Typography>
									<Typography
										variant="caption"
										color="text.secondary"
										sx={{ mb: 1, display: "block" }}
									>
										Map dataset columns to variable names
										used in steps as {"{{variableName}}"}
									</Typography>
									{(() => {
										const ds = dataSets.find(
											(d) =>
												d.id ===
												test.dataBinding?.dataSetId,
										);
										const columns = ds?.columns || [];
										const mapping =
											test.dataBinding?.columnMapping ||
											{};
										return columns.map((col) => (
											<Box
												key={col}
												sx={{
													display: "flex",
													gap: 1,
													mb: 1,
													alignItems: "center",
												}}
											>
												<Chip
													label={col}
													size="small"
													variant="outlined"
													sx={{ minWidth: 80 }}
												/>
												<Typography
													variant="body2"
													color="text.secondary"
												>
													→
												</Typography>
												<TextField disabled={readOnly}
													size="small"
													value={mapping[col] || col}
													onChange={(e) => {
														const newMapping = {
															...mapping,
															[col]: e.target
																.value,
														};
														setTest({
															...test,
															dataBinding: {
																...test.dataBinding,
																columnMapping:
																	newMapping,
															},
														});
													}}
													placeholder={col}
													sx={{ flex: 1 }}
												/>
											</Box>
										));
									})()}
								</>
							)}
						</CardContent>
					</Card>
				</Grid>
			</Grid>

			{/* Step Dialog */}
			<Dialog
				open={stepDialogOpen}
				onClose={handleCancelStep}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>
					{editingStepIndex !== null ? "Edit Step" : "Add Step"}
				</DialogTitle>
				<DialogContent>
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							gap: 2,
							mt: 1,
						}}
					>
						<FormControl fullWidth>
							<InputLabel>Step Type</InputLabel>
							<Select
								value={currentStep.type}
								label="Step Type"
								onChange={(e) =>
									setCurrentStep({
										...currentStep,
										type: e.target.value,
									})
								}
							>
								<MenuItem value="click">Click</MenuItem>
								<MenuItem value="doubleClick">Double Click</MenuItem>
								<MenuItem value="rightClick">Right Click</MenuItem>
								<MenuItem value="type">Type</MenuItem>
								<MenuItem value="input">Input</MenuItem>
								<MenuItem value="clearInput">Clear Input</MenuItem>
								<MenuItem value="change">Change</MenuItem>
								<MenuItem value="hover">Hover</MenuItem>
								<MenuItem value="keyboard">Keyboard</MenuItem>
								<MenuItem value="scroll">Scroll to Element</MenuItem>
								<MenuItem value="dragDrop">Drag & Drop</MenuItem>
								<MenuItem value="navigation">Navigate</MenuItem>
								<MenuItem value="newTab">Open New Tab</MenuItem>
								<MenuItem value="switchTab">Switch to Tab</MenuItem>
								<MenuItem value="wait">Wait</MenuItem>
								<MenuItem value="screenshot">
									Screenshot
								</MenuItem>
								<MenuItem value="select">Select</MenuItem>
								<Divider />
								<MenuItem value="assert-text">
									Assert Text
								</MenuItem>
								<MenuItem value="assert-value">
									Assert Value
								</MenuItem>
								<MenuItem value="assert-visible">
									Assert Visible
								</MenuItem>
								<MenuItem value="assert-not-visible">
									Assert Not Visible
								</MenuItem>
								<MenuItem value="assert-table">
									Assert Table Data
								</MenuItem>
								<MenuItem value="assert-url">
									Assert URL
								</MenuItem>
								<MenuItem value="assert-title">
									Assert Title
								</MenuItem>
								<MenuItem value="assert-count">
									Assert Count
								</MenuItem>
								<MenuItem value="assert-attribute">
									Assert Attribute
								</MenuItem>
								<Divider />
								<MenuItem value="visualCheck">Visual Check</MenuItem>
								<MenuItem value="accessibilityCheck">
									Accessibility Check
								</MenuItem>
							</Select>
						</FormControl>

						{currentStep.type === "navigation" ? (
							<>
								<TextField
									label={currentStep.fromClipboard ? "URL when recorded" : "URL"}
									fullWidth
									value={currentStep.url || ""}
									onChange={(e) =>
										setCurrentStep({
											...currentStep,
											url: e.target.value,
										})
									}
									placeholder="https://example.com"
									disabled={!!currentStep.fromClipboard}
									helperText={
										currentStep.fromClipboard
											? "Not used: each run opens the link it copied"
											: variableHint ||
												"Enter the URL to navigate to"
									}
								/>
								<FormControlLabel
									control={
										<Checkbox
											checked={!!currentStep.fromClipboard}
											onChange={(e) =>
												setCurrentStep({
													...currentStep,
													fromClipboard: e.target.checked || undefined,
												})
											}
										/>
									}
									label="Open the link copied earlier in the test (e.g. a share link)"
								/>
							</>
						) : currentStep.type === "switchTab" ? (
							<TextField
								label="Tab number"
								type="number"
								fullWidth
								value={currentStep.tabIndex >= 0 ? currentStep.tabIndex + 1 : ""}
								onChange={(e) =>
									setCurrentStep({
										...currentStep,
										tabIndex: e.target.value === "" ? undefined : Number(e.target.value) - 1,
									})
								}
								inputProps={{ min: 1 }}
								helperText="Tabs are numbered in the order they opened: the first tab is 1"
							/>
						) : !["assert-url", "assert-title", "keyboard", "newTab"].includes(currentStep.type) ? (
							<TextField
								label={
									CHECK_STEP_LABELS[currentStep.type]
										? "Selector (optional)"
										: "Selector"
								}
								fullWidth
								value={currentStep.selector || ""}
								onChange={(e) =>
									setCurrentStep({
										...currentStep,
										selector: e.target.value,
									})
								}
								placeholder="CSS selector or XPath"
								helperText={
									variableHint ||
									`e.g., #login-button, [data-testid="submit"], //button[@type="submit"]`
								}
								InputProps={
									editingStepIndex !== null &&
									isElementDialogStep(currentStep)
										? {
												endAdornment: (
													<InputAdornment position="end">
														<Tooltip describeChild title="Replay the steps before this one, then click the element in the browser">
															<Button
																size="small"
																startIcon={<AdsClick />}
																onClick={() =>
																	openReplay(
																		"pick",
																		editingStepIndex,
																		true,
																	)
																}
															>
																Pick element
															</Button>
														</Tooltip>
													</InputAdornment>
												),
											}
										: undefined
								}
							/>
						) : null}

						{["type", "input", "select", "change"].includes(
							currentStep.type,
						) && (
							<TextField
								label="Value"
								fullWidth
								value={currentStep.value || ""}
								onChange={(e) =>
									setCurrentStep({
										...currentStep,
										value: e.target.value,
									})
								}
								placeholder={
									hasDataBinding
										? "e.g. {{username}} or literal text"
										: "Text to enter"
								}
								helperText={variableHint}
							/>
						)}

						{currentStep.type === "assert-text" && (
							<TextField
								label="Expected Text"
								fullWidth
								value={currentStep.value || ""}
								onChange={(e) =>
									setCurrentStep({
										...currentStep,
										value: e.target.value,
									})
								}
								placeholder="Text content the element should contain"
								helperText={
									variableHint ||
									"Verifies the element's innerText matches this value"
								}
								multiline
								rows={2}
							/>
						)}

						{currentStep.type === "assert-value" && (
							<TextField
								label="Expected Value"
								fullWidth
								value={currentStep.value || ""}
								onChange={(e) =>
									setCurrentStep({
										...currentStep,
										value: e.target.value,
									})
								}
								placeholder="Value the input/select should have"
								helperText={
									variableHint ||
									"Verifies the element's value attribute (for inputs, selects, textareas)"
								}
							/>
						)}

						{currentStep.type === "assert-table" && (
							<TextField
								label="Expected Table Data (JSON)"
								fullWidth
								value={currentStep.value || ""}
								onChange={(e) =>
									setCurrentStep({
										...currentStep,
										value: e.target.value,
									})
								}
								placeholder='{"headers": ["Name", "Age"], "rows": [["Alice", "30"], ["Bob", "25"]]}'
								helperText='JSON with {"headers": [...], "rows": [[...], ...]}. Captured automatically when recording.'
								multiline
								rows={4}
							/>
						)}

						{currentStep.type === "wait" && (
							<TextField
								label="Wait Duration (ms)"
								type="number"
								fullWidth
								value={currentStep.value || "1000"}
								onChange={(e) =>
									setCurrentStep({
										...currentStep,
										value: e.target.value,
									})
								}
								placeholder="1000"
								helperText="Duration in milliseconds"
							/>
						)}

						{currentStep.type === "keyboard" && (
							<TextField
								label="Key / Shortcut"
								fullWidth
								value={currentStep.key || currentStep.value || ""}
								onChange={(e) =>
									setCurrentStep({
										...currentStep,
										key: e.target.value,
										value: e.target.value,
									})
								}
								placeholder="Enter, Tab, Escape, Control+S, ArrowDown"
								helperText="Playwright key name or modifier combo, e.g. Control+A"
							/>
						)}

						{currentStep.type === "dragDrop" && (
							<TextField
								label="Target Selector"
								fullWidth
								value={currentStep.targetSelector || currentStep.value || ""}
								onChange={(e) =>
									setCurrentStep({
										...currentStep,
										targetSelector: e.target.value,
										value: e.target.value,
									})
								}
								placeholder="CSS selector for the drop target"
								helperText="The element to drop onto"
							/>
						)}

						{currentStep.type === "assert-url" && (
							<TextField
								label="Expected URL (or substring)"
								fullWidth
								value={currentStep.value || ""}
								onChange={(e) =>
									setCurrentStep({
										...currentStep,
										value: e.target.value,
									})
								}
								placeholder="https://example.com/dashboard"
								helperText="Passes if the current URL contains this string"
							/>
						)}

						{currentStep.type === "assert-title" && (
							<TextField
								label="Expected Page Title (or substring)"
								fullWidth
								value={currentStep.value || ""}
								onChange={(e) =>
									setCurrentStep({
										...currentStep,
										value: e.target.value,
									})
								}
								placeholder="My App – Dashboard"
								helperText="Passes if the page <title> contains this string"
							/>
						)}

						{currentStep.type === "assert-count" && (
							<TextField
								label="Expected Count"
								type="number"
								fullWidth
								value={currentStep.value || ""}
								onChange={(e) =>
									setCurrentStep({
										...currentStep,
										value: e.target.value,
									})
								}
								placeholder="3"
								helperText="Number of elements matching the selector"
							/>
						)}

						{currentStep.type === "assert-attribute" && (
							<TextField
								label="Attribute=Value"
								fullWidth
								value={currentStep.value || ""}
								onChange={(e) =>
									setCurrentStep({
										...currentStep,
										value: e.target.value,
									})
								}
								placeholder="type=submit"
								helperText='Format: attributeName=expectedValue, e.g. aria-label=Close'
							/>
						)}

						<CheckStepFields
							step={currentStep}
							onChange={setCurrentStep}
							testId={id}
							readOnly={readOnly}
						/>

						{(currentStep.type || "").startsWith("assert") && (
							<TextField
								label="Description (optional)"
								fullWidth
								value={currentStep.text || ""}
								onChange={(e) =>
									setCurrentStep({
										...currentStep,
										text: e.target.value,
									})
								}
								placeholder="Description of what is being asserted"
								helperText="Optional description for better readability"
							/>
						)}
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCancelStep}>Cancel</Button>
					<Button onClick={handleSaveStep} variant="contained">
						{editingStepIndex !== null ? "Update" : "Add"}
					</Button>
				</DialogActions>
			</Dialog>

			<Menu
				anchorEl={stepMenu?.anchorEl}
				open={Boolean(stepMenu)}
				onClose={() => setStepMenu(null)}
			>
				<MenuItem onClick={() => openReplay("keep", stepMenu.index)}>
					Record after this step – keep the rest
				</MenuItem>
				<MenuItem onClick={() => openReplay("replace", stepMenu.index)}>
					Record after this step – replace the rest
				</MenuItem>
				<MenuItem onClick={() => openReplay("single", stepMenu.index)}>
					Re-record this step
				</MenuItem>
				{stepMenu && isElementStep(test.steps[stepMenu.index] || {}) && (
					<MenuItem onClick={() => openReplay("pick", stepMenu.index)}>
						Pick element for this step
					</MenuItem>
				)}
			</Menu>

			{replay && (
				<ReplayRecordDialog
					open
					mode={replay.mode}
					steps={test.steps}
					anchorIndex={replay.anchorIndex}
					startUrl={test.startUrl}
					dataBinding={test.dataBinding}
					onComplete={handleReplayComplete}
					onClose={() => setReplay(null)}
				/>
			)}

			<CloneDialog
				open={cloneOpen}
				kind="test"
				item={test}
				onClose={() => setCloneOpen(false)}
				onCloned={(clone) => navigate(`/tests/${clone.id}`)}
			/>

			{/* Create Folder Dialog */}
			<Dialog
				open={folderDialogOpen}
				onClose={() => {
					setFolderDialogOpen(false);
					setNewFolderName("");
				}}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>Create New Folder</DialogTitle>
				<DialogContent>
					<Box sx={{ pt: 2 }}>
						<TextField
							autoFocus
							label="Folder Name"
							fullWidth
							value={newFolderName}
							onChange={(e) => setNewFolderName(e.target.value)}
							placeholder="e.g., Authentication, Checkout"
							onKeyPress={(e) => {
								if (e.key === "Enter" && !creatingFolder) {
									handleCreateFolder();
								}
							}}
						/>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => {
							setFolderDialogOpen(false);
							setNewFolderName("");
						}}
						disabled={creatingFolder}
					>
						Cancel
					</Button>
					<Button
						onClick={handleCreateFolder}
						variant="contained"
						disabled={creatingFolder}
					>
						{creatingFolder ? (
							<CircularProgress size={20} />
						) : (
							"Create"
						)}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
}
